import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import Breadcrumbs from '../components/Breadcrumbs';

const InstallPackagesPage = () => {
  const apiPrefix = window.location.pathname.match(/^\/[^\/]+/) ? window.location.pathname.match(/^\/[^\/]+/)[0].replace(/\/$/, '') : '';
  const [packages, setPackages] = useState('');
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(localStorage.getItem('jwt_token') || '');
  const [loginLoading, setLoginLoading] = useState(false);

  useEffect(() => {
    if (authToken) {
      setIsAuthenticated(true);
    }
  }, [authToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setError('');

    try {
      const response = await fetch(apiPrefix + '/api/user-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Login failed');
      }

      const data = await response.json();
      localStorage.setItem('jwt_token', data.token); // Store token
      setAuthToken(data.token);
      setIsAuthenticated(true);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthToken('');
    setEmail('');
    setPassword('');
    setPackages('');
    setFeedback([]);
    setError('');
    localStorage.removeItem('jwt_token');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setFeedback([]);

    try {
      const response = await fetch(apiPrefix + '/api/install-packages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ packages: packages.split(/\s+/) }),
      });

      if (!response.ok) {
        // Try to parse as JSON for error messages
        try {
          const data = await response.json();
          throw new Error(data.error || 'Failed to install packages');
        } catch (jsonError) {
          // If JSON parsing fails, use status text
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let done = false;
      let buffer = '';

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        if (value) {
          const chunk = decoder.decode(value, { stream: !done });
          buffer += chunk;
          
          // Split by lines and update feedback
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer
          
          if (lines.length > 0) {
            setFeedback((prev) => [...prev, ...lines]);
          }
        }
      }
      
      // Add any remaining content in buffer
      if (buffer) {
        setFeedback((prev) => [...prev, buffer]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <header className="bg-white shadow-sm">
        <div className="container-fluid p-0">
          <img
            src="https://iis.memphis.edu/wp-content/uploads/2025/03/cropped-UofM_horiz_rgb.jpg"
            alt="Institute for Intelligent Systems Banner"
            className="img-fluid w-100"
            style={{ maxHeight: '90px', objectFit: 'contain', background: '#fff' }}
          />
        </div>
      </header>
      <div className="container py-4 flex-grow-1">
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Install Packages' }]} />
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card shadow-lg p-4">
              <h2 className="card-title text-center mb-3">Install LaTeX Packages</h2>
              <p className="card-subtitle text-muted text-center mb-4">Install additional LaTeX packages on all Overleaf instances.</p>
              {!isAuthenticated ? (
                <div className="card border-info mb-4">
                  <div className="card-header">
                    <h5 className="card-title">Login Required</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleLogin}>
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="password" className="form-label">Password</label>
                        <input
                          type="password"
                          className="form-control"
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                      </div>
                      <button type="submit" className="btn btn-primary" disabled={loginLoading}>
                        {loginLoading ? 'Logging in...' : 'Login'}
                      </button>
                    </form>
                  </div>
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="mb-0">Welcome! You are logged in.</p>
                    <button type="button" className="btn btn-outline-secondary" onClick={handleLogout}>
                      Logout
                    </button>
                  </div>
                  <form onSubmit={handleSubmit} className="mt-3">
                    <div className="mb-3">
                      <label htmlFor="packages" className="form-label">Packages (space-separated)</label>
                      <textarea
                        className="form-control"
                        id="packages"
                        rows={3}
                        value={packages}
                        onChange={(e) => setPackages(e.target.value)}
                        placeholder="Enter package names separated by spaces..."
                      />
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                      {loading ? 'Installing...' : 'Install Packages'}
                    </button>
                    {error && <div className="alert alert-danger mt-3">{error}</div>}
                  </form>
                  <div className="mt-4">
                    <h4>Feedback</h4>
                    <div className="bg-light p-3 border rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {feedback.length === 0 ? (
                        <p className="text-muted">No feedback yet.</p>
                      ) : (
                        feedback.map((line, index) => <p key={index} className="mb-1">{line}</p>)
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPackagesPage;
